// LiveRouteMap.jsx
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Escape any user-supplied string before injecting into Leaflet tooltips,
// which render their content as raw HTML.
const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const LiveRouteMap = ({ routes, isCustomerView = false }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      .custom-tooltip {
        background-color: rgba(0, 0, 0, 0.85) !important;
        color: white !important;
        border: 1px solid white !important;
        border-radius: 6px !important;
        font-size: 14px !important;
        padding: 8px !important;
        max-width: 300px !important;
        line-height: 1.4 !important;
      }
      .custom-tooltip::before { border-bottom-color: rgba(0, 0, 0, 0.85) !important; }
    `;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  useEffect(() => {
    if (!routes || routes.length === 0) {
      setIsLoading(false);
      return;
    }

    if (mapInstance.current) return;

    // Initialize the map
    const map = L.map(mapRef.current).setView(
      [routes[0].vehicle.coords[1], routes[0].vehicle.coords[0]],
      13
    );
    mapInstance.current = map;

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(map);

    // Add vehicle marker with tooltip (hover)
    L.marker(
      [routes[0].vehicle.coords[1], routes[0].vehicle.coords[0]],
      {
        icon: L.icon({
          iconUrl: 'https://cdn-icons-png.flaticon.com/512/2838/2838912.png',
          iconSize: [32, 32],
          iconAnchor: [16, 32],
        }),
      }
    )
    .addTo(map)
    .bindTooltip(`
      <div style="text-align: center;">
        <strong>${escapeHtml(routes[0].vehicle.name)}</strong><br>
        Driver: ${escapeHtml(routes[0].vehicle.driver)}<br>
        Capacity: ${escapeHtml(routes[0].vehicle.capacity)}kg<br>
        Total Load: ${escapeHtml(routes[0].totalWeight)}kg<br>
        Stops: ${routes[0].deliveries.length}
      </div>
    `, {
      permanent: false,
      direction: 'top',
      className: 'custom-tooltip'
    });

    // Add markers for each delivery (customer) with tooltip (hover)
    routes[0].deliveries.forEach((delivery) => {
      L.marker(
        [delivery.coords[1], delivery.coords[0]],
        {
          icon: L.icon({
            iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png',
            iconSize: [24, 24],
            iconAnchor: [12, 24],
          }),
        }
      )
      .addTo(map)
      .bindTooltip(`
        <div style="text-align: left;">
          <strong>${escapeHtml(delivery.name)}</strong><br>
          Priority: <span style="color: ${delivery.priority === 'high' ? 'red' : delivery.priority === 'medium' ? 'orange' : 'green'}">${escapeHtml(delivery.priority)}</span><br>
          Weight: ${escapeHtml(delivery.weight)}kg<br>
          Time: ${escapeHtml(delivery.timeWindow.start)}:00 - ${escapeHtml(delivery.timeWindow.end)}:00<br>
          <strong>Vehicle:</strong> ${escapeHtml(routes[0].vehicle.name)} (${escapeHtml(routes[0].vehicle.driver)})
        </div>
      `, {
        permanent: false,
        direction: 'top',
        className: 'custom-tooltip'
      });
    });

    // Draw the route line
    const routePoints = [
      [routes[0].vehicle.coords[1], routes[0].vehicle.coords[0]],
      ...routes[0].deliveries.map(d => [d.coords[1], d.coords[0]])
    ];
    L.polyline(routePoints, { color: 'blue', weight: 4 }).addTo(map);

    // Fit the map to the route bounds
    const bounds = new L.LatLngBounds(routePoints);
    map.fitBounds(bounds, { padding: [50, 50] });

    setIsLoading(false);

    return () => {
      if (map) {
        map.remove();
        mapInstance.current = null;
      }
    };
  }, [routes]);

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          padding: '15px',
          borderRadius: '5px',
          zIndex: 1000,
        }}>
          Loading map...
        </div>
      )}
      <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};

export default LiveRouteMap;
